import React, { memo } from 'react';
import { Layout, Menu } from 'antd';
import { useLocation } from 'react-router-dom';
import { useNavigation } from '@/shared/hooks/useNavigation';
import { layoutStyles } from '@/shared/styles/layoutStyles';
import caryLogoBlack from '@/assets/cary-logo-black.png';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  width?: number;
  collapsedWidth?: number;
  onNavigate?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = memo(({
  collapsed,
  width = 240,
  collapsedWidth = 80,
  onNavigate
}) => {
  const location = useLocation();
  const { primaryMenuItems, secondaryMenuItems } = useNavigation();

  const getSelectedKeys = (menuItems: Array<{ key: string }>, pathname: string) => {
    const selectedKey = menuItems.find(item => {
      const itemKey = item.key;
      if (itemKey === pathname) return true;
      if (itemKey !== '/' && pathname.startsWith(itemKey)) return true;
      return false;
    })?.key;
    return selectedKey ? [selectedKey] : [];
  };

  // Wrap menu items to include onNavigate callback
  const wrapMenuItems = (items: typeof primaryMenuItems) => {
    return items.map(item => ({
      ...item,
      onClick: () => {
        item.onClick?.();
        onNavigate?.();
      },
    }));
  };

  const wrappedPrimaryMenuItems = onNavigate ? wrapMenuItems(primaryMenuItems) : primaryMenuItems;
  const wrappedSecondaryMenuItems = onNavigate ? wrapMenuItems(secondaryMenuItems) : secondaryMenuItems;

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      style={layoutStyles.sider}
      width={width}
      collapsedWidth={collapsedWidth}
    >
      {/* Logo Section */}
      <div style={layoutStyles.logoContainer}>
        <img
          src={caryLogoBlack}
          alt="Cary Group"
          style={layoutStyles.logo}
        />
      </div>

      {/* Navigation Container */}
      <div style={layoutStyles.navigationContainer}>
        {/* Primary Navigation */}
        <div style={layoutStyles.primaryNavigation}>
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={getSelectedKeys(primaryMenuItems, location.pathname)}
            items={wrappedPrimaryMenuItems}
            style={layoutStyles.primaryMenu}
          />
        </div>

        {/* Secondary Navigation - at bottom */}
        <div style={layoutStyles.secondaryNavigation}>
          <div style={layoutStyles.menuSeparator} />
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={getSelectedKeys(secondaryMenuItems, location.pathname)}
            items={wrappedSecondaryMenuItems}
            style={layoutStyles.menu}
          />
        </div>
      </div>
    </Sider>
  );
});

Sidebar.displayName = 'Sidebar';